import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { InspectionType, AdvisorType } from "../constants/settingsStructure";

/**
 * Get the persistence state key for draft changes based on prompt type
 *
 * @param inspectionType - The inspection type (for context cards: workmanship or site_safety)
 * @param advisorType - The advisor type (for field advisor cards: analyze, execute, or review)
 * @returns The corresponding persistence state key
 */
export const getDraftKey = (
  inspectionType?: InspectionType,
  advisorType?: AdvisorType
): keyof typeof PERSISTENCE_STATE_KEYS => {
  if (advisorType) {
    if (advisorType === "analyze") return "draftFieldAdvisorPromptAnalyze";
    if (advisorType === "execute") return "draftFieldAdvisorPromptExecute";
    return "draftFieldAdvisorPromptReview";
  }
  return inspectionType === "workmanship"
    ? "draftContextPromptWorkmanship"
    : "draftContextPromptSiteSafety";
};

/**
 * Get the category key for a given prompt type
 *
 * @param inspectionType - The inspection type (for context cards)
 * @param advisorType - The advisor type (for field advisor cards)
 * @returns The category key used to identify the settings category
 */
export const getCategoryKey = (
  inspectionType?: InspectionType,
  advisorType?: AdvisorType
): string => {
  if (advisorType) {
    if (advisorType === "analyze") return "analyzePrompt";
    if (advisorType === "execute") return "executePrompt";
    return "reviewPrompt";
  }
  return inspectionType === "workmanship" ? "workmanshipPrompt" : "siteSafetyPrompt";
};

/**
 * Get the human-readable display name for a prompt type
 *
 * @param inspectionType - The inspection type (for context cards)
 * @param advisorType - The advisor type (for field advisor cards)
 * @returns The display name for the prompt type
 */
export const getDisplayName = (
  inspectionType?: InspectionType,
  advisorType?: AdvisorType
): string => {
  if (advisorType) {
    return advisorType.charAt(0).toUpperCase() + advisorType.slice(1);
  }
  return inspectionType === "workmanship" ? "workmanship" : "site & safety";
};

/**
 * Get the publish state key for tracking whether publish button should appear
 * These keys are separate from draft keys which are used by backend for text descriptions
 *
 * @param inspectionType - The inspection type (for context cards: workmanship or site_safety)
 * @param advisorType - The advisor type (for field advisor cards: analyze, execute, or review)
 * @returns The corresponding publish state key
 */
export const getPublishKey = (
  inspectionType?: InspectionType,
  advisorType?: AdvisorType
): keyof typeof PERSISTENCE_STATE_KEYS => {
  if (advisorType) {
    if (advisorType === "analyze") return "isPublishFieldAdvisorPromptAnalyze";
    if (advisorType === "execute") return "isPublishFieldAdvisorPromptExecute";
    return "isPublishFieldAdvisorPromptReview";
  }
  return inspectionType === "workmanship"
    ? "isPublishContextPromptWorkmanship"
    : "isPublishContextPromptSiteSafety";
};
